import { useAuth } from '@clerk/clerk-react';
import { useTrpcMutation, useTrpcQuery } from '@vrplatform/data';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import type { Maybe } from '@vrplatform/utils';
import { Box, Tooltip, useMantineTheme } from '@mantine/core';
import { HiddenFeatureIndicator } from '../../../components';
import { useOwnerDetailDrawer } from '../useOwnerDetailDrawer';
import type { OwnerDetail } from '../useOwnerDetailDrawerQuery';

type Props = {
  openDeleteModal: () => void;
  openArchiveModal: () => void;
  openEmailModal: () => void;
  openCopyLinkModal: () => void;
  openRevokeAccessModal: () => void;
  contact: {
    id: string;
    isArchived: boolean;
    email: Maybe<string>;
    userAccess: OwnerDetail['userAccess'];
    connectionId: Maybe<string>;
    tenantId: string;
  };
};

export const OwnerEllipsisMenuItems = ({
  openDeleteModal,
  openArchiveModal,
  openEmailModal,
  openCopyLinkModal,
  openRevokeAccessModal,
  contact,
}: Props) => {
  const { open } = useOwnerDetailDrawer();

  const theme = useMantineTheme();

  const access = contact.userAccess?.status || 'inactive';

  return (
    <>
      <EllipsisMenuItem
        onClick={() => open(contact.id, 'edit')}
        icon="Edit3Icon"
      >
        Edit
      </EllipsisMenuItem>

      {access === 'inactive' && (
        <EllipsisMenuItem icon="EmailIcon" onClick={openEmailModal}>
          Invite to Owner Portal
        </EllipsisMenuItem>
      )}

      {access === 'active' && contact.userAccess?.userId && (
        <Impersonation
          tenantId={contact.tenantId}
          contactId={contact.id}
          userId={contact.userAccess.userId}
        />
      )}

      {access !== 'inactive' && (
        <>
          <EllipsisMenuItem icon="EmailIcon" onClick={openEmailModal}>
            Resend invitation
          </EllipsisMenuItem>

          <EllipsisMenuItem icon="Link2Icon" onClick={openCopyLinkModal}>
            Copy invitation link
          </EllipsisMenuItem>
        </>
      )}

      <EllipsisMenuDivider />

      {access !== 'inactive' && (
        <EllipsisMenuItem
          icon="CrossCircleIcon"
          iconColor={theme.colors.orange[6]}
          onClick={openRevokeAccessModal}
        >
          Revoke portal access
        </EllipsisMenuItem>
      )}

      <EllipsisMenuItem
        onClick={openArchiveModal}
        icon={contact.isArchived ? 'RefreshCwIcon' : 'ArchiveIcon'}
      >
        {contact.isArchived ? 'Unarchive' : 'Archive'}
      </EllipsisMenuItem>

      <Tooltip
        disabled={!contact.connectionId}
        label={'This owner is linked to a PMS and cannot be deleted.'}
        withArrow
        multiline
        withinPortal
        maw={400}
      >
        <Box>
          <EllipsisMenuDangerItem
            onClick={openDeleteModal}
            disabled={!!contact.connectionId}
          >
            Delete
          </EllipsisMenuDangerItem>
        </Box>
      </Tooltip>

      <HiddenFeatureIndicator permission="super-admin">
        <EllipsisMenuCopyItem value={contact.id} />
      </HiddenFeatureIndicator>
    </>
  );
};

const Impersonation = ({
  tenantId,
  contactId,
  userId,
}: {
  tenantId: string;
  contactId: string;
  userId: string;
}) => {
  const { signOut } = useAuth();

  const { data, loading: loadingQuery } = useTrpcQuery(
    'fetchClerkUser',
    {
      teamId: tenantId,
      userId: userId,
    },
    {
      skip: !userId || !tenantId,
    }
  );

  const hasAccess = !!(
    (data?.publicMetadata?.allowed_ids || '') as string
  )?.includes(tenantId);

  const { mutate, loading } = useTrpcMutation('impersonateUser', {
    successMessage: {
      title: 'Owner Preview',
      message: 'Successfully created owner preview.',
    },
  });

  return (
    <Tooltip
      disabled={hasAccess || loadingQuery}
      label={'Owner is not yet active on the owner portal'}
      withArrow
    >
      <Box>
        <EllipsisMenuItem
          onClick={() =>
            mutate({ userId, tenantId }).then((res) => {
              const redirectUrl = new URL(res.url, window.location.origin);
              redirectUrl.searchParams.set('sti', tenantId);
              redirectUrl.searchParams.set('cti', contactId);

              signOut({
                redirectUrl: redirectUrl.toString(),
              });
            })
          }
          icon="EyeIcon"
          loading={loading || loadingQuery}
          disabled={!hasAccess}
        >
          Owner Portal
        </EllipsisMenuItem>
      </Box>
    </Tooltip>
  );
};
